import "date-fns";
import React, { useState } from "react";
import { injectIntl, IntlShape } from "react-intl";
import { gql, useMutation } from "@apollo/client";
import moment from "moment";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
} from "@material-ui/core";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import Button from "../button";
import GenericSelect from "../atoms/GenericSelect";
import { WEEKDAYS, getExerciseTypeLabels } from "../pre-season-shared/constants";
import messages from "./PageTeamPreSeason.intl";

export const CREATE_PRE_SEASON_EXERCISE = gql`
  mutation CreatePreSeasonExercise($input: PreSeasonExerciseInput!) {
    createPreSeasonExercise(input: $input) {
      id
    }
  }
`;

interface ISquadMember {
  id: string;
  name: string;
  surname: string;
}

interface ICreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  teamId: string;
  squad: ISquadMember[];
  intl: IntlShape;
}

const CreatePreSeasonExerciseDialog = injectIntl<
  Omit<ICreateDialogProps, "intl">
>(({ open, onClose, onCreated, teamId, squad, intl }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("RUNNING");
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(
    moment().add(4, "days").toDate()
  );
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [assignment, setAssignment] = useState<"ALL" | "SELECTED">("ALL");
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);

  const [createExercise, { loading }] = useMutation(
    CREATE_PRE_SEASON_EXERCISE
  );

  const typeLabels = getExerciseTypeLabels(intl);

  const toggleWeekday = (value: number) => {
    setWeekdays((current) =>
      current.includes(value)
        ? current.filter((day) => day !== value)
        : [...current, value]
    );
  };

  const togglePlayer = (playerId: string) => {
    setSelectedPlayers((current) =>
      current.includes(playerId)
        ? current.filter((id) => id !== playerId)
        : [...current, playerId]
    );
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setType("RUNNING");
    setStartDate(new Date());
    setEndDate(moment().add(4, "days").toDate());
    setWeekdays([1, 2, 3, 4, 5]);
    setAssignment("ALL");
    setSelectedPlayers([]);
  };

  const handleSubmit = async () => {
    if (!title || !startDate || !endDate || weekdays.length === 0) {
      return;
    }

    await createExercise({
      variables: {
        input: {
          teamId,
          title,
          description: description || undefined,
          type,
          startDate: moment(startDate).format("YYYY-MM-DD"),
          endDate: moment(endDate).format("YYYY-MM-DD"),
          weekdays,
          playerIds:
            assignment === "SELECTED" && selectedPlayers.length > 0
              ? selectedPlayers
              : undefined,
        },
      },
    });

    resetForm();
    onCreated();
  };

  const canSubmit =
    title.trim().length > 0 &&
    startDate &&
    endDate &&
    weekdays.length > 0 &&
    (assignment === "ALL" || selectedPlayers.length > 0);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{intl.formatMessage(messages.createDialogTitle)}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={intl.formatMessage(messages.fieldTitle)}
              placeholder={intl.formatMessage(messages.fieldTitlePlaceholder)}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              label={intl.formatMessage(messages.fieldDescription)}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <GenericSelect
              id="pre-season-exercise-type"
              label={intl.formatMessage(messages.fieldType)}
              collection={typeLabels}
              value={type}
              onChange={(e: any) => {
                if (e) setType(e.target.value);
              }}
            />
          </Grid>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid item xs={6}>
              <KeyboardDatePicker
                fullWidth
                format="dd/MM/yyyy"
                margin="normal"
                label={intl.formatMessage(messages.fieldStartDate)}
                value={startDate}
                onChange={(date) => setStartDate(date)}
                KeyboardButtonProps={{ "aria-label": "change start date" }}
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDatePicker
                fullWidth
                format="dd/MM/yyyy"
                margin="normal"
                label={intl.formatMessage(messages.fieldEndDate)}
                value={endDate}
                minDate={startDate || undefined}
                onChange={(date) => setEndDate(date)}
                KeyboardButtonProps={{ "aria-label": "change end date" }}
              />
            </Grid>
          </MuiPickersUtilsProvider>
          <Grid item xs={12}>
            <FormLabel component="legend" style={{ marginBottom: 8 }}>
              {intl.formatMessage(messages.fieldWeekdays)}
            </FormLabel>
            <div>
              {WEEKDAYS.map((day) => (
                <Chip
                  key={day.value}
                  label={intl.formatMessage(messages[day.messageKey])}
                  clickable
                  color={weekdays.includes(day.value) ? "primary" : "default"}
                  onClick={() => toggleWeekday(day.value)}
                  style={{ marginRight: 8, marginBottom: 8 }}
                />
              ))}
            </div>
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                {intl.formatMessage(messages.assignmentTitle)}
              </FormLabel>
              <RadioGroup
                value={assignment}
                onChange={(e) => setAssignment(e.target.value as "ALL" | "SELECTED")}
              >
                <FormControlLabel
                  value="ALL"
                  control={<Radio />}
                  label={intl.formatMessage(messages.assignmentAll)}
                />
                <FormControlLabel
                  value="SELECTED"
                  control={<Radio />}
                  label={intl.formatMessage(messages.assignmentSelected)}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {assignment === "SELECTED" && (
            <Grid item xs={12}>
              <FormGroup>
                {squad.map((player) => (
                  <FormControlLabel
                    key={player.id}
                    control={
                      <Checkbox
                        checked={selectedPlayers.includes(player.id)}
                        onChange={() => togglePlayer(player.id)}
                      />
                    }
                    label={`${player.name} ${player.surname}`}
                  />
                ))}
              </FormGroup>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {intl.formatMessage(messages.cancel)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
        >
          {intl.formatMessage(messages.create)}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default CreatePreSeasonExerciseDialog;
